/**
 * TenantTopbar — pasek górny tenant shell'a (F7b T7b.8).
 *
 * Różnice vs admin `Topbar.tsx`:
 *  - Czyta `useTenantAuth()` (nie `useAuth()` — ten woła AuthContext, który nie jest
 *    mountowany w `TenantApp`).
 *  - Tytuł: `me.tenant.name` (nazwa firmy) zamiast statycznego "SaaS v2 — panel admina".
 *  - Brak role badge (tenant user nie ma roli admina; w F11 dojdzie permissions chip).
 *  - Logout: `logout()` z TenantAuthContext + navigate('/login') (tenant scope login).
 *
 * Menu użytkownika (2026-08, jak SellCall): klik w imię/nazwisko (prawy górny róg) otwiera
 * dropdown „Konto" → „Profil" (/profile) + „Wyloguj". Profil jest tu dostępny dla KAŻDEGO
 * usera, niezależnie od uprawnień do Ustawień.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, LogOut, UserRound } from 'lucide-react';

import { BugReportButton } from '@/components/bug-report/BugReportButton';
import { Button } from '@/components/Button';
import { GlobalSearch } from '@/components/tenant/GlobalSearch';
import { GlobalSearchModal } from '@/components/tenant/GlobalSearchModal';
import { TenantSwitcher } from '@/components/tenant/TenantSwitcher';
// ThemePresetToggle ukryty na razie (patrz niżej) — przywróć import przy odkomentowaniu.
import { ThemeToggle } from '@/components/ThemeToggle';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { buildCentralLoginUrl } from '@/lib/tenant-url';

export function TenantTopbar() {
  const { t } = useTranslation();
  const { me, logout } = useTenantAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Globalny skrót Ctrl/Cmd+K — otwiera paletę wyszukiwarki z dowolnego miejsca powłoki.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Click-outside zamyka menu użytkownika.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (ev: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const onLogout = async () => {
    await logout(); // czyści token, dev-tenant-id, aktywną firmę
    // ADR-0075 — po wylogowaniu ZAWSZE na centralny ekran logowania (z wyborem firmy),
    // nie na logowanie per-subdomena. Full nav (cross-scope) + reset sticky scope w DEV.
    window.location.assign(buildCentralLoginUrl());
  };

  const displayName = me?.user.displayName ?? me?.user.email ?? '';
  const initial = displayName.trim().charAt(0).toUpperCase() || '?';

  return (
    <>
      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      <header
        className="flex h-[50px] w-full flex-none items-center gap-3"
        data-testid="tenant-topbar"
      >
        {/* Figma (123:24680): lewa strona pusta (firma w sidebarze); wyszukiwarka wyśrodkowana
            elastycznymi spacerami; po prawej dark + dzwonek + chip usera. */}
        <div className="min-w-0 flex-1" aria-hidden="true" />
        <div className="w-[440px] max-w-[440px] shrink-0">
          <GlobalSearch layout="inline" />
        </div>

        <div className="flex flex-1 items-center justify-end gap-3">
          <TenantSwitcher />
          <ThemeToggle />

          {/* Zgłoś błąd — robaczek otwiera pływający widget zgłoszeń (portal). */}
          <BugReportButton />

          {/* Dzwonek powiadomień (Figma „Notifications") — OKRĄGŁY biały przycisk 40px, ikona 20.
              Panel/licznik powiadomień jeszcze nie istnieje (brak backendu) — PLACEHOLDER:
              poza kolejnością Tab i `aria-disabled`, żeby nie wpuszczać fokusa/czytnika na
              martwą kontrolkę (dojdzie handler, gdy powstanie panel). */}
          <div
            role="button"
            tabIndex={-1}
            aria-disabled="true"
            aria-label={t('tenant.topbar.notifications')}
            title={t('tenant.topbar.notifications')}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border-secondary bg-bg-elevated text-text-secondary shadow-[0_1px_1px_rgba(23,23,33,0.06)]"
            data-testid="tenant-notifications"
          >
            <Bell size={20} aria-hidden="true" />
          </div>

          {me && (
            <div className="relative" ref={menuRef}>
              {/* Chip usera wg Figmy „User chip" (73:8709): bg subtelny, radius 12, pl-6/pr-12,
                  awatar 36px (biały, radius 12, kolorowy inicjał), nazwa 13/20, email 12/18, chevron 20. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="!h-12 gap-2.5 !rounded-full border border-border-secondary bg-bg-elevated py-0 pl-1.5 pr-3 shadow-[0_1px_2px_rgba(23,23,33,0.06)] hover:bg-bg-tertiary"
                data-testid="tenant-user-menu-toggle"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#e7e5fa] text-sm font-semibold text-[#5751af]">
                  {initial}
                </span>
                <span className="hidden text-left sm:block">
                  <span className="block text-sm font-medium leading-[22px] text-text-primary">
                    {me.user.displayName ?? me.user.email}
                  </span>
                  {me.user.displayName && (
                    <span className="block text-xs leading-[18px] text-text-tertiary">
                      {me.user.email}
                    </span>
                  )}
                </span>
                <ChevronDown size={16} aria-hidden="true" />
              </Button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-lg border border-border-secondary bg-bg-elevated py-1 shadow-lg"
                  data-testid="tenant-user-menu"
                >
                  <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                    {t('tenant.settings.groups.account')}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/profile');
                    }}
                    leftIcon={<UserRound size={16} aria-hidden="true" />}
                    className="w-full justify-start rounded-none"
                    data-testid="tenant-user-menu-profile"
                  >
                    {t('tenant.settings.tabs.profile')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    role="menuitem"
                    onClick={onLogout}
                    leftIcon={<LogOut size={16} aria-hidden="true" />}
                    className="w-full justify-start rounded-none text-red-600 dark:text-red-400"
                    data-testid="tenant-user-menu-logout"
                  >
                    {t('tenant.topbar.logout')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
}
